import { useNavigate } from 'react-router-dom';
import { Shield, Users, TrendingUp, AlertTriangle, DollarSign, Award, Activity, Target, Heart, Crosshair, Zap, ChevronRight, Hexagon } from 'lucide-react';

function CommandDashboard({ data, user }) {
  const navigate = useNavigate();
  const personnel = data?.personnel_overview || {};
  const promotions = data?.promotions_panel || {};
  const discipline = data?.discipline_summary || {};
  const finance = data?.finance_snapshot || {};
  const metrics = data?.strategic_metrics || {};
  const alerts = data?.alerts || [];

  // Get doctrine info
  const doctrine = user?.doctrine_context || user?.doctrine || {};
  const isGDT = doctrine?.doctrine_type === 'GDT' || doctrine?.is_gdt;

  return (
    <div className="space-y-6" data-testid="command-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <h1 className="text-3xl font-bold text-white tracking-wide" style={{ fontFamily: 'ui-monospace, monospace', textShadow: '0 0 20px rgba(6, 182, 212, 0.4)' }}>
              COMMAND CENTER
            </h1>
            <span className={`doctrine-badge ${isGDT ? 'doctrine-gdt' : 'doctrine-pist'}`}>
              <Hexagon size={12} />
              {isGDT ? 'GDT' : 'PIST'}
            </span>
          </div>
          <p className="text-slate-500 text-sm tracking-wider">STRATEGIC OPERATIONS OVERVIEW</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="rank-badge rank-command flex items-center gap-2">
            <Shield size={14} />
            COMMAND AUTHORITY
          </span>
        </div>
      </div>

      {/* Alerts Panel - Critical */}
      {alerts.length > 0 && <AlertsPanel alerts={alerts} navigate={navigate} />}

      {/* Top Stats - Data Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DataCard 
          icon={Users} 
          label="Total Personnel" 
          value={personnel.total || 0} 
          color="cyan"
        />
        <DataCard 
          icon={Activity} 
          label="Active" 
          value={personnel.active || 0} 
          color="emerald"
          glowing
        />
        <DataCard 
          icon={AlertTriangle} 
          label="Suspended" 
          value={personnel.suspended || 0} 
          color="red"
        />
        <DataCard 
          icon={Target} 
          label="On Leave" 
          value={personnel.on_leave || 0} 
          color="amber"
        />
      </div>

      {/* Three Column Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PromotionsPanel data={promotions} navigate={navigate} />
        <DisciplinePanel data={discipline} navigate={navigate} />
        <FinancePanel data={finance} navigate={navigate} />
      </div>

      {/* Strategic Metrics */}
      <div className="glass-panel p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-sm font-bold text-cyan-400 tracking-[0.15em]">STRATEGIC METRICS</h2>
          <span className="text-xs text-slate-600 font-mono">REAL-TIME DATA</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <MetricCard value={`${metrics.training_completion_rate || 0}%`} label="Training" icon={TrendingUp} color="blue" />
          <MetricCard value={`${metrics.operational_readiness || 0}%`} label="Readiness" icon={Target} color="emerald" />
          <MetricCard value={metrics.deployment_eligible || 0} label="Deploy Ready" icon={Shield} color="amber" />
          <MetricCard value={metrics.medical_cleared || 0} label="Medical OK" icon={Heart} color="purple" />
          <MetricCard value={metrics.weapons_cleared || 0} label="Weapons OK" icon={Crosshair} color="red" />
        </div>
      </div>
    </div>
  );
}

function DataCard({ icon: Icon, label, value, color, glowing }) {
  const colorStyles = {
    cyan: { bg: 'rgba(6, 182, 212, 0.1)', border: 'rgba(6, 182, 212, 0.3)', text: '#06b6d4', shadow: 'rgba(6, 182, 212, 0.3)' },
    emerald: { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', text: '#10b981', shadow: 'rgba(16, 185, 129, 0.3)' },
    red: { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', text: '#ef4444', shadow: 'rgba(239, 68, 68, 0.3)' },
    amber: { bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', text: '#f59e0b', shadow: 'rgba(245, 158, 11, 0.3)' },
  };
  const style = colorStyles[color] || colorStyles.cyan;

  return (
    <div 
      className={`data-card ${glowing ? 'animate-pulse-cyan' : ''}`}
      style={{ borderColor: style.border }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
        <Icon size={16} style={{ color: style.text }} />
      </div>
      <div className="text-3xl font-bold font-mono" style={{ color: style.text, textShadow: `0 0 20px ${style.shadow}` }}>
        {value}
      </div>
    </div>
  );
}

function MetricCard({ value, label, icon: Icon, color }) {
  const colorMap = {
    blue: 'text-blue-400',
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    purple: 'text-purple-400',
    red: 'text-red-400',
  };

  return (
    <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-800/50 text-center hover:border-cyan-500/30 transition-colors">
      <Icon size={20} className={`mx-auto mb-2 ${colorMap[color]}`} />
      <div className={`text-2xl font-bold font-mono ${colorMap[color]}`}>{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function AlertsPanel({ alerts, navigate }) {
  return (
    <div className="glass-panel p-5 border-red-500/30 animate-pulse-danger" style={{ background: 'rgba(239, 68, 68, 0.05)' }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-red-400 flex items-center gap-2 tracking-wider">
          <Zap size={16} />
          ACTION ITEMS REQUIRING ATTENTION
        </h2>
        <span className="text-xs font-mono text-red-400">{alerts.length} PENDING</span>
      </div>
      <div className="space-y-2">
        {alerts.slice(0, 5).map((alert, idx) => (
          <AlertRow key={idx} alert={alert} navigate={navigate} />
        ))}
      </div>
    </div>
  );
}

function AlertRow({ alert, navigate }) {
  const priorityStyles = {
    critical: 'alert-critical',
    high: 'alert-warning',
    medium: 'alert-advisory'
  };
  const badgeClass = priorityStyles[alert.priority] || priorityStyles.medium;

  const getRoute = (type) => {
    const routes = { appeal: '/disciplinary', incident: '/deployments', overdue_query: '/disciplinary' };
    return routes[type] || '/disciplinary';
  };

  return (
    <div className="p-3 rounded-lg bg-red-950/30 border border-red-900/30 flex justify-between items-start hover:bg-red-950/50 transition-colors">
      <div>
        <div className="flex items-center space-x-2 mb-1">
          <span className={`alert-badge ${badgeClass}`}>
            {alert.priority.toUpperCase()}
          </span>
          <span className="text-slate-500 text-xs uppercase tracking-wider">{alert.type.replace('_', ' ')}</span>
        </div>
        <p className="text-white text-sm font-medium">{alert.title}</p>
        {alert.description && <p className="text-slate-500 text-xs mt-1 truncate max-w-md">{alert.description}</p>}
      </div>
      <button 
        onClick={() => navigate(getRoute(alert.type))}
        className="btn-tactical text-xs py-1.5 px-3 flex items-center gap-1"
      >
        VIEW <ChevronRight size={12} />
      </button>
    </div>
  );
}

function PromotionsPanel({ data, navigate }) {
  return (
    <div className="glass-panel overflow-hidden">
      <div className="p-4 border-b border-slate-800/50 flex items-center justify-between bg-slate-900/30">
        <h3 className="text-sm font-bold text-cyan-400 tracking-wider flex items-center gap-2">
          <Award size={16} />
          PROMOTIONS
        </h3>
        <button onClick={() => navigate('/promotions')} className="text-xs text-slate-400 hover:text-cyan-400 flex items-center gap-1">
          VIEW ALL <ChevronRight size={12} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Pending</span>
          <span className="text-xl font-mono font-bold text-amber-400">{data.pending || 0}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Approved (30d)</span>
          <span className="text-xl font-mono font-bold text-emerald-400">{data.approved_this_month || 0}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Rejected (30d)</span>
          <span className="text-xl font-mono font-bold text-red-400">{data.rejected_this_month || 0}</span>
        </div>
      </div>
    </div>
  );
}

function DisciplinePanel({ data, navigate }) {
  return (
    <div className="glass-panel overflow-hidden">
      <div className="p-4 border-b border-slate-800/50 flex items-center justify-between bg-slate-900/30">
        <h3 className="text-sm font-bold text-cyan-400 tracking-wider flex items-center gap-2">
          <AlertTriangle size={16} />
          DISCIPLINE
        </h3>
        <button onClick={() => navigate('/disciplinary')} className="text-xs text-slate-400 hover:text-cyan-400 flex items-center gap-1">
          VIEW ALL <ChevronRight size={12} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Active Cases</span>
          <span className="text-xl font-mono font-bold text-red-400">{data.active_cases || 0}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Pending Appeals</span>
          <span className="text-xl font-mono font-bold text-amber-400">{data.pending_appeals || 0}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Overdue Queries</span>
          <span className="text-xl font-mono font-bold text-orange-400">{data.overdue_queries || 0}</span>
        </div>
      </div>
    </div>
  );
}

function FinancePanel({ data, navigate }) {
  const formatCurrency = (val) => {
    if (!val) return '$0';
    return `$${val.toLocaleString()}`;
  };

  return (
    <div className="glass-panel overflow-hidden">
      <div className="p-4 border-b border-slate-800/50 flex items-center justify-between bg-slate-900/30">
        <h3 className="text-sm font-bold text-cyan-400 tracking-wider flex items-center gap-2">
          <DollarSign size={16} />
          FINANCE
        </h3>
        <button onClick={() => navigate('/finance')} className="text-xs text-slate-400 hover:text-cyan-400 flex items-center gap-1">
          VIEW ALL <ChevronRight size={12} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Monthly Payroll</span>
          <span className="text-xl font-mono font-bold text-cyan-400">{formatCurrency(data.monthly_payroll)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Client Revenue</span>
          <span className="text-xl font-mono font-bold text-emerald-400">{formatCurrency(data.client_revenue)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-slate-400 text-sm">Pending Deposits</span>
          <span className="text-xl font-mono font-bold text-amber-400">{data.pending_deposits || 0}</span>
        </div>
      </div>
    </div>
  );
}

export default CommandDashboard;
